import { Button } from '@chiroup/components/Button';
import { Input } from '@chiroup/components/form/Input';
import { Select } from '@chiroup/components/form/Select';
import { FormError } from '@chiroup/core/types/ErrorResponse.type';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import useDevtools from '../../../../hooks/useDevtools';

type BillingProfileOption = {
  value: string;
  text: string;
  active?: number;
  sortKey?: string;
};

type FormData = {
  billingProfileId: string;
  salesforceCase: string;
  mondayStory: string;
};

type Props = {
  clinicId: number;
};

const ACCEPTED_EXTENSIONS = '.835,.rmt,.277,.27c,.27p,.999,.271';

const EdiUploadPanel: React.FC<Props> = ({ clinicId }) => {
  const { getActiveBillingProfiles } = useDevtools('billing-panel') as any;
  const { upload, isFetching } = useDevtools('edi-upload') as any;

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: { billingProfileId: '', salesforceCase: '', mondayStory: '' },
  });

  const [activeBillingProfiles, setActiveBillingProfiles] = useState<BillingProfileOption[]>([]);
  const [isLoadingProfiles, setIsLoadingProfiles] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [result, setResult] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadBillingProfiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicId]);

  const loadBillingProfiles = async () => {
    setIsLoadingProfiles(true);

    try {
      const response = await getActiveBillingProfiles({ clinicId });

      if (response.active) {
        const sorted = [...response.active].sort((a: any, b: any) =>
          (a.sortKey || '').localeCompare(b.sortKey || '')
        );
        setActiveBillingProfiles(sorted);
      }
    } catch (err: any) {
      setError(`Failed to load billing profiles: ${err.message || 'Unknown error'}`);
    } finally {
      setIsLoadingProfiles(false);
    }
  };

  // FileReader gives "data:...;base64,XXXX"; the backend strips the prefix.
  const readFileAsBase64 = (f: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(f);
    });

  const onSubmit = async (data: FormData) => {
    setResult(null);
    setError(null);

    if (!file) {
      setFileError('Choose a file to upload.');
      return;
    }

    try {
      const fileBase64 = await readFileAsBase64(file);
      const res = await upload({
        clinicId,
        billingProfileId: parseInt(data.billingProfileId, 10),
        salesforceCase: data.salesforceCase.trim(),
        mondayStory: data.mondayStory.trim(),
        filename: file.name,
        fileBase64,
      });
      setResult(res);
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'An error occurred.');
    }
  };

  return (
    <div className="space-y-8">
      <div className="bg-gray-100 border border-gray-300 rounded-lg p-4">
        <p className="text-sm">
          <strong>Clinic ID:</strong> {clinicId}
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <section className="bg-white border border-gray-400 inset-4 rounded-lg">
          <div className="p-4 flex flex-row justify-between">
            <h1 className="font-extrabold">Upload EDI Response File</h1>
            <Button
              text="Upload & Process"
              disabled={isFetching || isLoadingProfiles}
              loading={isFetching}
              type="submit"
            />
          </div>

          <div className="p-4 m-4 border border-gray-400 bg-gray-50 rounded-lg text-sm space-y-2">
            <p>
              Upload a single EDI response file and process it immediately — no clearinghouse
              retrieval, no waiting for the drop watcher. Useful for testing and one-off fixes.
            </p>
            <p>
              <strong>Supported files:</strong> .835 / .rmt (ERA), .277 / .27c / .27p (claim
              acknowledgment), .999 (implementation acknowledgment), .271 (eligibility).
            </p>
            <p className="text-red-600">
              <strong>Warning:</strong> This posts against real clinic data. An 835 drains inline
              and applies payments/adjustments. Use with care.
            </p>
          </div>

          <div className="p-4 flex flex-col space-y-4">
            <Controller
              name="billingProfileId"
              control={control}
              rules={{ required: 'Required.' }}
              render={({ field }) => (
                <Select
                  {...field}
                  label="Billing profile"
                  options={[
                    { value: '', text: '-- Select a billing profile --' },
                    ...activeBillingProfiles.map(bp => ({ value: bp.value, text: bp.text })),
                  ]}
                  disabled={isFetching || isLoadingProfiles}
                  errors={
                    errors.billingProfileId
                      ? ({ message: errors.billingProfileId.message } as FormError)
                      : undefined
                  }
                />
              )}
            />

            <Controller
              name="salesforceCase"
              control={control}
              rules={{ required: 'Required.' }}
              render={({ field }) => (
                <Input
                  {...field}
                  label="Salesforce Case Number"
                  disabled={isFetching}
                  errors={
                    errors.salesforceCase
                      ? ({ message: errors.salesforceCase.message } as FormError)
                      : undefined
                  }
                />
              )}
            />

            <Controller
              name="mondayStory"
              control={control}
              rules={{ required: 'Required.' }}
              render={({ field }) => (
                <Input
                  {...field}
                  label="Monday Story Number"
                  disabled={isFetching}
                  errors={
                    errors.mondayStory
                      ? ({ message: errors.mondayStory.message } as FormError)
                      : undefined
                  }
                />
              )}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">EDI file</label>
              <input
                type="file"
                accept={ACCEPTED_EXTENSIONS}
                disabled={isFetching}
                onChange={e => {
                  setFile(e.target.files?.[0] ?? null);
                  setFileError(null);
                  setResult(null);
                  setError(null);
                }}
                className="block w-full text-sm text-gray-700 file:mr-4 file:rounded-md file:border-0 file:bg-primary-50 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-primary-700 hover:file:bg-primary-100"
              />
              {fileError && <p className="mt-1 text-sm text-red-600">{fileError}</p>}
            </div>
          </div>

          {error && (
            <div className="p-4 m-4 border border-red-400 bg-red-50 rounded-lg text-sm">
              <p className="font-semibold text-red-800">Error: {error}</p>
            </div>
          )}

          {result && (
            <div className="p-4 m-4 border border-green-400 bg-green-50 rounded-lg text-sm space-y-2">
              <p className="font-semibold text-green-800">
                Processed as {result.routeExtension}
                {result.isDuplicate ? ' (duplicate file — already seen)' : ''}. Exchange{' '}
                {result.exchangeId}.
              </p>
              <pre className="overflow-x-auto rounded bg-white/70 p-3 text-xs text-gray-800">
                {JSON.stringify(result.result, null, 2)}
              </pre>
            </div>
          )}
        </section>
      </form>
    </div>
  );
};

export default EdiUploadPanel;
